import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { followsAPI } from '../api';
import { createSlug } from '../utils/slug';

interface FollowedUser {
  id: number;
  username: string;
  email: string;
  profile_picture?: string;
  background_image?: string;
  followed_at: string;
}

interface FollowedStore {
  id: number;
  name: string;
  address: string;
  profile_picture?: string;
  background_image?: string;
  city_name?: string;
  state_name?: string;
  state_abbreviation?: string;
  followed_at: string;
}

const Followed: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [followedUsers, setFollowedUsers] = useState<FollowedUser[]>([]);
  const [followedStores, setFollowedStores] = useState<FollowedStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'stores'>('users');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadFollowed();
  }, [isAuthenticated]);

  const loadFollowed = async () => {
    try {
      setLoading(true);
      const [users, stores] = await Promise.all([
        followsAPI.getFollowingUsers(),
        followsAPI.getFollowingStores(),
      ]);
      setFollowedUsers(users);
      setFollowedStores(stores);
    } catch (err: any) {
      console.error('Error loading followed items:', err);
      toast.error(err.response?.data?.error || 'Failed to load followed items');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollowUser = async (userId: number) => {
    try {
      await followsAPI.unfollowUser(userId);
      setFollowedUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to unfollow user');
    }
  };

  const handleUnfollowStore = async (storeId: number) => {
    try {
      await followsAPI.unfollowStore(storeId);
      setFollowedStores(prev => prev.filter(s => s.id !== storeId));
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to unfollow store');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Following</h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'users'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Users ({followedUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('stores')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'stores'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Stores ({followedStores.length})
          </button>
        </div>

        {/* Followed Users */}
        {activeTab === 'users' && (
          <div>
            {followedUsers.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-4">👥</div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">No followed users</h2>
                <p className="text-gray-600">Start following sellers to see their latest listings</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {followedUsers.map((user) => (
                  <div key={user.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <Link to={`/sellers/${user.id}`} className="block">
                      {user.background_image ? (
                        <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${user.background_image})` }}></div>
                      ) : (
                        <div className="h-32 bg-gradient-to-r from-green-400 to-green-600"></div>
                      )}
                    </Link>
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <Link to={`/sellers/${user.id}`}>
                          {user.profile_picture ? (
                            <img
                              src={user.profile_picture}
                              alt={user.username}
                              className="w-16 h-16 rounded-full border-2 border-white object-cover -mt-8 shadow-md"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full border-2 border-white bg-green-500 flex items-center justify-center text-white text-xl font-bold -mt-8 shadow-md">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/sellers/${user.id}`}>
                            <h3 className="text-lg font-semibold text-gray-900 hover:text-green-600 transition truncate">
                              {user.username}
                            </h3>
                          </Link>
                          <p className="text-xs text-gray-500 mt-1">Followed {formatDate(user.followed_at)}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUnfollowUser(user.id);
                          }}
                          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm font-medium transition"
                        >
                          Unfollow
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Followed Stores */}
        {activeTab === 'stores' && (
          <div>
            {followedStores.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <div className="text-6xl mb-4">🏪</div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">No followed stores</h2>
                <p className="text-gray-600">Start following stores to see their latest items</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {followedStores.map((store) => (
                  <div key={store.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <Link to={`/stores/${createSlug(store.name)}`} className="block">
                      {store.background_image ? (
                        <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${store.background_image})` }}></div>
                      ) : (
                        <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                      )}
                    </Link>
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <Link to={`/stores/${createSlug(store.name)}`}>
                          {store.profile_picture ? (
                            <img
                              src={store.profile_picture}
                              alt={store.name}
                              className="w-16 h-16 rounded-full border-2 border-white object-cover -mt-8 shadow-md"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full border-2 border-white bg-green-500 flex items-center justify-center text-white text-xl -mt-8 shadow-md">
                              <i className="ri-store-line"></i>
                            </div>
                          )}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/stores/${createSlug(store.name)}`}>
                            <h3 className="text-lg font-semibold text-gray-900 hover:text-green-600 transition truncate">
                              {store.name}
                            </h3>
                          </Link>
                          <p className="text-xs text-gray-500 mt-1 truncate">{store.address}</p>
                          {store.city_name && store.state_abbreviation && (
                            <p className="text-xs text-gray-500">{store.city_name}, {store.state_abbreviation}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">Followed {formatDate(store.followed_at)}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUnfollowStore(store.id);
                          }}
                          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm font-medium transition"
                        >
                          Unfollow
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Followed;

